import { useEffect, useRef, useState } from 'react'
import { price as fmtPrice, priceLike, signedPct } from '../core/format'
import type { Quote } from '../core/types'
import { TokenIcon } from './primitives/Media'
import { Sparkline } from './primitives/Sparkline'

/** Flash the row green or red for one frame when the print changes. */
function useFlash(value: number) {
  const prev = useRef(value)
  const [dir, setDir] = useState<'up' | 'down' | null>(null)
  useEffect(() => {
    if (value === prev.current) return
    setDir(value > prev.current ? 'up' : 'down')
    prev.current = value
    const t = window.setTimeout(() => setDir(null), 420)
    return () => window.clearTimeout(t)
  }, [value])
  return dir
}

function QuoteCard({ q }: { q: Quote }) {
  const flash = useFlash(q.price)
  const up = q.change24hPct >= 0
  const color = up ? 'var(--up)' : 'var(--down)'
  return (
    <div className={`quote ${flash ? `quote--${flash}` : ''}`}>
      <span className="quote__icon">
        <TokenIcon symbol={q.symbol} size={26} />
      </span>
      <div className="quote__id">
        <span className="quote__label">{q.label}</span>
        <span className="quote__note">{q.note}</span>
      </div>
      <div className="quote__px">
        <span className="quote__price u-num">{fmtPrice(q.price)}</span>
        <span className={`quote__chg u-num ${up ? 'u-up' : 'u-down'}`}>
          {signedPct(q.change24hPct)}
        </span>
      </div>
      <div className="quote__spark" style={{ color }}>
        <Sparkline data={q.spark} color={color} width={108} height={30} />
      </div>
      {q.high24h > 0 ? (
        <div className="quote__hl">
          <span className="u-label">H</span>
          <span className="u-num">{priceLike(q.price, q.high24h)}</span>
          <span className="u-label">L</span>
          <span className="u-num">{priceLike(q.price, q.low24h)}</span>
        </div>
      ) : (
        // A reconstructed index has no true 24h high or low. An empty slot
        // reads cleaner than a lonely dash, and the column stays aligned.
        <div className="quote__hl quote__hl--none" aria-hidden="true" />
      )}
    </div>
  )
}

export function ChainRail({ quotes }: { quotes: Quote[] }) {
  if (!quotes.length) {
    return (
      <div className="chainrail chainrail--empty">
        <span className="u-label">CONNECTING TO PRICE FEED</span>
      </div>
    )
  }
  return (
    <div className="chainrail">
      {quotes.map((q) => (
        <QuoteCard key={q.symbol} q={q} />
      ))}
    </div>
  )
}
